import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { Button, Col, Form, Modal } from 'react-bootstrap';
import { StyleSheet, css } from 'aphrodite';
import Select from 'react-select';
import SwalDialog from 'lib/utils/toast';

import {
  GLOBAL_MANUFACTURERS_FOR_DROPDOWN,
  GLOBAL_VENDORS_FOR_DROPDOWN,
  USER_TIME_LOG_CREATE,
} from 'lib/networking/endpoints';
import LoadingSpinner from 'components/shared/LoadingSpinner';
import { ProductType, ProductTypeOptions } from 'lib/enums/ProductType';
import SearchableScrollableSelect from 'components/shared/SearchableScrollableSelect';
import { spinnerOverlayGrey } from 'lib/css/colors';
import {
  outputRequiredForActivityType,
  UserTimeLogActivityTypeOptions,
} from 'components/settings/user_time_logs/UserTimeLogEnums';
import usePost from 'lib/hooks/usePost';
function UserTimeLogModal({ onHide }) {
  const [logData, setlogData] = useState({
    date: new Date().toISOString().split('T')[0],
    activity_type: '',
    entity_type: '',
    time_spent: '',
    description: '',
    global_vendors: [],
    global_manufacturers: [],
    output: null,
  });
  const [hoursSpent, setHoursSpent] = useState('');

  const { postData: createTimeLog, loading: creatingTimeLog } = usePost(
    USER_TIME_LOG_CREATE,
    () => {
      SwalDialog('success', 'Time log created successfully', 'Success', 'center');
      onHide(true);
    },
    error => {
      SwalDialog(
        'error',
        'An error occurred while adding the time log: ' + error?.response?.data?.message,
        'Error',
        'center',
      );
    },
  );

  const handleSubmit = () => {
    const newLog = {
      date: logData.date,
      activity_type: logData.activity_type,
      time_spent: parseFloat(logData.time_spent),
      description: logData.description,
      global_vendor_ids:
        logData.entity_type === ProductType.DISTRIBUTOR
          ? logData.global_vendors.map(vendor => vendor.value)
          : null,
      global_manufacturer_ids:
        logData.entity_type === ProductType.MANUFACTURER
          ? logData.global_manufacturers.map(manufacturer => manufacturer.value)
          : null,
      output: logData.output,
    };
    createTimeLog(newLog);
  };
  return (
    <Modal show={true} onHide={() => onHide(false)} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title className={css(styles.heading)}>Record Time</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <>
          {creatingTimeLog && (
            <div className={css(styles.spinnerOverlay)}>
              <LoadingSpinner />
            </div>
          )}
          <>
            <div className={css(styles.section)}>
              <Form.Row>
                <Form.Group as={Col}>
                  <Form.Label className="font-weight-bold">Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={logData.date}
                    onChange={e => setlogData(prev => ({ ...prev, date: e.target.value }))}
                    max={new Date().toISOString().split('T')[0]}
                  />
                </Form.Group>
                <Form.Group as={Col}>
                  <Form.Label className="font-weight-bold">Type of Work</Form.Label>
                  <Select
                    options={UserTimeLogActivityTypeOptions}
                    value={
                      UserTimeLogActivityTypeOptions.find(
                        option => option.value === logData.activity_type,
                      ) || null
                    }
                    onChange={option =>
                      setlogData(prev => ({
                        ...prev,
                        activity_type: option?.value || '',
                      }))
                    }
                    className="basic-select"
                    classNamePrefix="select"
                    isClearable
                  />
                </Form.Group>
              </Form.Row>
            </div>
            <div className={css(styles.section)}>
              <Form.Row>
                <Form.Group as={Col}>
                  <Form.Label className="font-weight-bold">Entity Type</Form.Label>
                  <Select
                    options={ProductTypeOptions}
                    value={
                      ProductTypeOptions.find(option => option.value === logData.entity_type) ||
                      null
                    }
                    onChange={option =>
                      setlogData(prev => ({
                        ...prev,
                        entity_type: option.value,
                        global_vendor: {},
                        global_manufacturer: {},
                      }))
                    }
                    className="basic-select"
                    classNamePrefix="select"
                  />
                </Form.Group>
                <Form.Group as={Col}>
                  <Form.Label className="font-weight-bold">Entity Name</Form.Label>
                  {logData.entity_type === ProductType.DISTRIBUTOR ? (
                    <SearchableScrollableSelect
                      key={ProductType.DISTRIBUTOR}
                      initOption={logData.global_vendors}
                      onChange={option =>
                        setlogData(prev => ({
                          ...prev,
                          global_vendors: option || [],
                        }))
                      }
                      fetchUrl={GLOBAL_VENDORS_FOR_DROPDOWN}
                      optionMapper={option => ({ label: option.name, value: option.id })}
                      disabled={!logData.entity_type}
                      isMulti={true}
                    />
                  ) : (
                    <SearchableScrollableSelect
                      key={ProductType.MANUFACTURER}
                      initOption={logData.global_manufacturers}
                      onChange={option =>
                        setlogData(prev => ({
                          ...prev,
                          global_manufacturers: option || [],
                        }))
                      }
                      fetchUrl={GLOBAL_MANUFACTURERS_FOR_DROPDOWN}
                      optionMapper={option => ({ label: option.name, value: option.id })}
                      disabled={!logData.entity_type}
                      isMulti={true}
                    />
                  )}
                </Form.Group>
              </Form.Row>
            </div>
            {outputRequiredForActivityType.includes(logData.activity_type) && (
              <div className={css(styles.section)}>
                <Form.Row>
                  <Form.Group as={Col}>
                    <Form.Label className="font-weight-bold">Output</Form.Label>
                    <Form.Control
                      type="number"
                      placeholder="Enter output"
                      value={logData.output}
                      onChange={e => setlogData(prev => ({ ...prev, output: e.target.value }))}
                    />
                  </Form.Group>
                </Form.Row>
              </div>
            )}
            <div className={css(styles.section)}>
              <Form.Group>
                <Form.Label className="font-weight-bold">Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={logData.description}
                  onChange={e =>
                    setlogData(prev => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                />
              </Form.Group>
            </div>
            <div className={css(styles.section)}>
              <Form.Group>
                <Form.Label className="font-weight-bold">Hours Spent (HH:MM)</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter time as HH:MM"
                  value={hoursSpent}
                  onChange={e => {
                    const input = e.target.value;
                    const timeFormat = /^(\d{0,2}):?([0-5]?[0-9])?$/;

                    if (!timeFormat.test(input)) {
                      SwalDialog(
                        'error',
                        'Invalid Format, Please enter time in HH:MM format. Example - 02:34.',
                        'Error',
                        'center',
                      );
                      return;
                    }

                    const [hours, minutes] = input.split(':').map(Number);
                    const totalSeconds = (hours || 0) * 3600 + (minutes || 0) * 60;
                    setlogData(prev => ({
                      ...prev,
                      time_spent: totalSeconds,
                    }));
                    setHoursSpent(input);
                  }}
                />
              </Form.Group>
            </div>
          </>
        </>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="primary" onClick={handleSubmit} disabled={creatingTimeLog}>
          Save
        </Button>
        <Button variant="outline-secondary" onClick={() => onHide(false)}>
          Cancel
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

const styles = StyleSheet.create({
  heading: {
    fontWeight: 'bold',
    fontFamily: 'Mont, sans-serif',
    fontSize: '1.5rem',
  },
  section: {
    border: '1px solid #ccc',
    borderRadius: '5px',
    padding: '8px',
    marginBottom: '10px',
  },
  spinnerOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: spinnerOverlayGrey,
    zIndex: 1,
  },
});

UserTimeLogModal.propTypes = {
  onHide: PropTypes.func.isRequired,
};

export default UserTimeLogModal;
